import React, { useRef, useEffect } from "react";
import * as THREE from "three";

const textures = [
  { id: "baseColor", img: "/assets/textures/grey_sphere.jpg" },
  { id: "blueLeatherTexture", img: "/assets/textures/blue_sphere.jpg" },
  { id: "lightBlueFabricTexture", img: "/assets/textures/sky_sphere.jpg" },
  { id: "lightGreyFabricTexture", img: "/assets/textures/white_sphere.jpg" },
];

const OUTLINE_COLOR = 0xffffff;
const OUTLINE_WIDTH = 0.06;

function isDesktop() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(hover: hover) and (pointer: fine)").matches
  );
}

const ThreeJSSelector = ({ onSelect, activeMaterialId, visible }) => {
  const mountRef = useRef();
  const outlinesRef = useRef([]);
  const spheresRef = useRef([]);
  const hoveredIndexRef = useRef(null);

  useEffect(() => {
    const width = 500;
    const height = 140;
    const aspect = width / height;
    const camera = new THREE.OrthographicCamera(
      -aspect * 2,
      aspect * 2,
      2,
      -2,
      0.1,
      100
    );
    camera.position.z = 10;

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setClearColor(0x000000, 0); // transparent
    renderer.setSize(width, height);
    mountRef.current.appendChild(renderer.domElement);

    // Scene & lights
    const scene = new THREE.Scene();
    scene.add(new THREE.AmbientLight(0xffffff, 0.7));
    const dirLight = new THREE.DirectionalLight(0xffffff, 0.7);
    dirLight.position.set(0, 5, 5);
    scene.add(dirLight);

    // Load textures & create spheres
    const loader = new THREE.TextureLoader();
    const spheres = [];
    const outlines = [];
    textures.forEach((tex, i) => {
      const geometry = new THREE.SphereGeometry(1.0, 64, 64);
      const material = new THREE.MeshStandardMaterial({
        map: loader.load(tex.img),
        metalness: 0.5,
        roughness: 0.5,
      });
      const sphere = new THREE.Mesh(geometry, material);
      sphere.position.x = (i - 1.5) * 2.5;
      sphere.userData = { id: tex.id, index: i };
      scene.add(sphere);
      spheres.push(sphere);

      // Outline
      const outlineGeo = new THREE.SphereGeometry(1.0 + OUTLINE_WIDTH, 64, 64);
      const outlineMat = new THREE.MeshBasicMaterial({
        color: OUTLINE_COLOR,
        side: THREE.BackSide,
      });
      const outline = new THREE.Mesh(outlineGeo, outlineMat);
      outline.position.copy(sphere.position);
      outline.visible = tex.id === activeMaterialId;
      scene.add(outline);
      outlines.push(outline);
    });
    outlinesRef.current = outlines;
    spheresRef.current = spheres;

    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();

    function onClick(event) {
      const rect = renderer.domElement.getBoundingClientRect();
      mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(mouse, camera);
      const intersects = raycaster.intersectObjects(spheres);
      if (intersects.length > 0) {
        const id = intersects[0].object.userData.id;
        if (onSelect) onSelect(id);
      }
    }
    renderer.domElement.addEventListener("click", onClick);

    function onPointerMove(event) {
      const rect = renderer.domElement.getBoundingClientRect();
      mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(mouse, camera);
      const intersects = raycaster.intersectObjects(spheres);
      if (intersects.length > 0) {
        hoveredIndexRef.current = intersects[0].object.userData.index;
      } else {
        hoveredIndexRef.current = null;
      }
    }
    if (isDesktop()) {
      renderer.domElement.addEventListener("mousemove", onPointerMove);
      renderer.domElement.addEventListener("mouseleave", () => {
        hoveredIndexRef.current = null;
      });
    }

    // Animation loop
    function animate() {
      spheres.forEach((s, i) => {
        // console.log('activeMaterialId', activeMaterialId, textures[i].id);
        if (textures[i].id === activeMaterialId) {
          s.rotation.y += 0.03;
          s.scale.set(1.18, 1.18, 1.18);
        } else {
          s.scale.set(1, 1, 1);
        }
      });
      outlines.forEach((outline, i) => {
        if (textures[i].id === activeMaterialId) {
          outline.visible = true;
          outline.scale.set(1.18, 1.18, 1.18);
        } else {
          outline.visible = false;
        }
      });
      renderer.render(scene, camera);
      requestAnimationFrame(animate);
    }
    animate();

    // Cleanup
    return () => {
      renderer.domElement.removeEventListener("click", onClick);
      if (isDesktop()) {
        renderer.domElement.removeEventListener("mousemove", onPointerMove);
        renderer.domElement.removeEventListener("mouseleave", () => {
          hoveredIndexRef.current = null;
        });
      }
      if (renderer.domElement && renderer.domElement.parentNode) {
        renderer.domElement.parentNode.removeChild(renderer.domElement);
      }
      renderer.forceContextLoss && renderer.forceContextLoss();
      renderer.dispose();
    };
    // eslint-disable-next-line
  }, [onSelect]);

  useEffect(() => {
    outlinesRef.current.forEach((outline, i) => {
      outline.visible = textures[i].id === activeMaterialId;
    });
  }, [activeMaterialId]);

  return (
    <div
      ref={mountRef}
      style={{
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? "auto" : "none",
        transition: "opacity 0.2s",
        position: "absolute",
        left: "50%",
        bottom: 40,
        transform: "translateX(-50%)",
        zIndex: 10000,
        width: 500,
        height: 140,
      }}
    />
  );
};

export default ThreeJSSelector;
